import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import { useAnimationFrameCallback } from '../../../hooks/useAnimationFrameCallback';
import { CategoryChip } from '../CategoryChip';
const SKELETON_COUNT = 7;
const DEFAULT_ALL_COLOR = 'var(--clt-skyline)';
const DEFAULT_ALL_FOREGROUND = '#FFFFFF';
const DEFAULT_ARIA_LABEL = 'Filter by category';
const CONTAINER_CLASSES = [
    'flex gap-2',
    'overflow-x-auto snap-x snap-mandatory scrollbar-hide',
    'md:flex-wrap md:overflow-visible md:snap-none',
].join(' ');
function LoadingSkeleton({ className, ariaLabel, }) {
    return (_jsx("div", { "data-testid": "category-chip-strip-loading", role: "status", "aria-label": `${ariaLabel} loading`, className: className, children: Array.from({ length: SKELETON_COUNT }).map((_, index) => (_jsx("div", { "aria-hidden": "true", className: "h-11 w-24 shrink-0 animate-pulse rounded-full bg-white/[0.08] backdrop-blur-[14px] motion-reduce:animate-none" }, `category-chip-skeleton-${index}`))) }));
}
/**
 * CategoryChipStrip — horizontal chip filter with an `All` chip plus a
 * caller-provided list of categories.
 *
 * Implements the WAI-ARIA tablist pattern with roving `tabIndex`, arrow-key
 * navigation (wraps), and Enter/Space activation. In RTL the arrow
 * directions are swapped (ArrowLeft advances, ArrowRight retreats). The
 * direction is read from the nearest ancestor that sets `dir`, falling back
 * to the document root.
 *
 * Responsive: horizontal scroll with snap on mobile, wraps on `md+`.
 */
export function CategoryChipStrip({ selected, onSelect, categories, allIcon, allLabel = 'All', allChipColor = DEFAULT_ALL_COLOR, allChipForeground = DEFAULT_ALL_FOREGROUND, loading = false, ariaLabel = DEFAULT_ARIA_LABEL, className = '', ...rest }) {
    const containerRef = useRef(null);
    const buttonRefs = useRef([]);
    const chips = useMemo(() => {
        return [
            {
                slug: null,
                label: allLabel,
                icon: allIcon,
                color: allChipColor,
                foreground: allChipForeground,
            },
            ...categories.map((category) => ({
                slug: category.slug,
                label: category.label,
                icon: category.icon,
                color: category.color,
                foreground: category.foreground,
            })),
        ];
    }, [allChipColor, allChipForeground, allIcon, allLabel, categories]);
    const activeIndex = useMemo(() => {
        const idx = chips.findIndex((chip) => chip.slug === selected);
        return idx === -1 ? 0 : idx;
    }, [chips, selected]);
    const containerClasses = [CONTAINER_CLASSES, className].filter(Boolean).join(' ');
    const scheduleFrame = useAnimationFrameCallback();
    const focusChipAt = useCallback((index) => {
        // Defer so React commits the new tabIndex before focus moves.
        scheduleFrame(() => {
            buttonRefs.current[index]?.focus();
        });
    }, [scheduleFrame]);
    const handleKeyDown = useCallback((event) => {
        if (chips.length === 0)
            return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            const dir = containerRef.current?.closest('[dir]')?.getAttribute('dir') ??
                (typeof document !== 'undefined' ? document.documentElement.dir : 'ltr');
            const forward = dir === 'rtl' ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
            const delta = forward ? 1 : -1;
            const nextIndex = (activeIndex + delta + chips.length) % chips.length;
            onSelect(chips[nextIndex].slug);
            focusChipAt(nextIndex);
            return;
        }
        if (event.key === 'Home') {
            event.preventDefault();
            onSelect(chips[0].slug);
            focusChipAt(0);
            return;
        }
        if (event.key === 'End') {
            event.preventDefault();
            const lastIndex = chips.length - 1;
            onSelect(chips[lastIndex].slug);
            focusChipAt(lastIndex);
        }
    }, [activeIndex, chips, focusChipAt, onSelect]);
    if (loading) {
        return _jsx(LoadingSkeleton, { className: containerClasses, ariaLabel: ariaLabel });
    }
    return (_jsx("div", { ref: containerRef, role: "tablist", "aria-label": ariaLabel, "data-testid": "category-chip-strip", onKeyDown: handleKeyDown, className: containerClasses, ...rest, children: chips.map((chip, index) => {
            const isActive = index === activeIndex;
            const key = chip.slug ?? '__all__';
            return (_jsx(CategoryChip, { ref: (el) => {
                    buttonRefs.current[index] = el;
                }, icon: chip.icon, label: chip.label, isActive: isActive, activeColor: chip.color, activeForeground: chip.foreground, tabIndex: isActive ? 0 : -1, "data-testid": chip.slug === null ? 'category-chip-all' : `category-chip-${chip.slug}`, onClick: () => onSelect(chip.slug) }, key));
        }) }));
}
CategoryChipStrip.displayName = 'CategoryChipStrip';
